import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  HeartIcon, 
  ShareIcon, 
  FlagIcon, 
  MapPinIcon, 
  ClockIcon, 
  UserIcon, 
  ChatBubbleLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  StarIcon,
  ShieldCheckIcon,
  EyeIcon,
  PrinterIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  TagIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { 
  HeartIcon as HeartIconSolid, 
  StarIcon as StarIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid
} from '@heroicons/react/24/solid';
import api, { adsApi, AdAttribute, userApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { getImageUrl } from '../utils/imageUtils';
import TimchehLoader from '../components/TimchehLoader';
import '../styles/promotional-effects.css';

// Category-specific template interfaces (simplified)
interface CategoryTemplate {
  name: string;
  layout: 'default' | 'automotive' | 'electronics' | 'realestate' | 'fashion' | 'twocolumn' | 'threecolumn';
  sections: { [key: string]: string[] }; // Dynamic sections based on attribute groups
}

const CATEGORY_TEMPLATES: { [key: string]: CategoryTemplate } = {
  'automotive': {
    name: 'خودرو',
    layout: 'automotive',
    sections: {
      'اطلاعات پایه': ['year', 'make', 'model', 'trim', 'vin'],
      'عملکرد': ['mileage', 'engine', 'fueltype', 'transmission', 'drivetrain'],
      'جزئیات': ['bodytype', 'doors', 'exteriorcolor', 'interiorcolor', 'features']
    }
  },
  'electronics': {
    name: 'الکترونیک',
    layout: 'electronics',
    sections: {
      'اطلاعات دستگاه': ['brand', 'model', 'screensize', 'resolution', 'storage', 'ram'],
      'ویژگی‌ها': ['operatingsystem', 'processor', 'connectivity', 'warranty', 'accessories']
    }
  },
  'realestate': {
    name: 'املاک',
    layout: 'realestate',
    sections: {
      'اطلاعات ملک': ['propertytype', 'bedrooms', 'bathrooms', 'sqft', 'lotsize'],
      'ویژگی‌ها': ['yearbuilt', 'parking', 'heating', 'cooling', 'flooring'],
      'امکانات': ['amenities', 'appliances', 'utilities', 'petpolicy', 'furnished']
    }
  },
  'fashion': {
    name: 'مد و پوشاک',
    layout: 'fashion',
    sections: {
      'اطلاعات محصول': ['brand', 'size', 'color', 'material', 'pattern'],
      'جزئیات': ['gender', 'season', 'occasion', 'careinstructions', 'measurements']
    }
  }
};

// Dynamic category component that renders based on actual attributes
const DynamicCategorySpecs: React.FC<{ 
  categoryName: string; 
  attributes: AdAttribute[];
  layout: string;
}> = ({ categoryName, attributes, layout }) => {
  if (!attributes.length) return null;

  const template = getCategoryTemplate(categoryName);
  const sections = template.sections;

  // Group attributes by section based on attribute names
  const groupedAttributes: { [section: string]: AdAttribute[] } = {};
  
  // First, categorize attributes based on their names
  attributes.forEach(attr => {
    const attrNameLower = attr.name.toLowerCase().replace(/\s+/g, '');
    let assignedSection = 'سایر';
    
    // Find which section this attribute belongs to
    for (const [sectionName, fieldNames] of Object.entries(sections)) {
      if (fieldNames.some(field => attrNameLower.includes(field.toLowerCase()))) {
        assignedSection = sectionName;
        break;
      }
    }
    
    if (!groupedAttributes[assignedSection]) {
      groupedAttributes[assignedSection] = [];
    }
    groupedAttributes[assignedSection].push(attr);
  });

  // If no attributes were categorized, put them all in a single section
  if (Object.keys(groupedAttributes).length === 0 || (Object.keys(groupedAttributes).length === 1 && groupedAttributes['سایر'])) {
    groupedAttributes['مشخصات'] = attributes;
    delete groupedAttributes['سایر'];
  }

  const getLayoutClass = () => {
    switch (layout) {
      case 'automotive':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      case 'realestate':
        return 'grid-cols-1 md:grid-cols-3';
      case 'electronics':
      case 'fashion':
        return 'grid-cols-1 md:grid-cols-2';
      default:
        return 'grid-cols-1 md:grid-cols-2';
    }
  };

  const getIcon = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('car') || name.includes('auto') || name.includes('vehicle')) return '🚗';
    if (name.includes('electronics') || name.includes('phone') || name.includes('computer')) return '📱';
    if (name.includes('real') || name.includes('property') || name.includes('house')) return '🏠';
    if (name.includes('fashion') || name.includes('clothing') || name.includes('apparel')) return '👗';
    if (name.includes('home') || name.includes('garden') || name.includes('furniture')) return '🏡';
    return '📋';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
        <span className="mr-2">{getIcon(categoryName)}</span>
        جزئیات {template.name}
      </h2>
      <div className={`grid ${getLayoutClass()} gap-6`}>
        {Object.entries(groupedAttributes).map(([sectionName, sectionAttrs]) => (
          <div key={sectionName} className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2">{sectionName}</h3>
            {sectionAttrs.map(attr => (
              <div key={attr.id} className="flex justify-between items-start">
                <span className="text-gray-600 text-sm font-medium">{attr.label}:</span>
                <span className="text-gray-800 font-semibold text-sm mr-2 text-right flex-1">
                  {attr.value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Simplified selling points component using dynamic attributes
const CategorySellingPoints: React.FC<{ 
  categoryName: string; 
  attributes: AdAttribute[]; 
  price: number; 
}> = ({ categoryName, attributes, price }) => {
  const template = getCategoryTemplate(categoryName);
  
  const generateSellingPoints = () => {
    const points: { icon: string; text: string; highlight?: boolean }[] = [];
    
    // Dynamic highlighting based on attributes
    attributes.forEach(attr => {
      const name = attr.name.toLowerCase();
      const value = attr.value.toLowerCase();
      
      // Check for condition-related attributes
      if (name.includes('condition')) {
        if (value.includes('new')) {
          points.push({ icon: '✨', text: 'نو', highlight: true });
        } else if (value.includes('like new')) {
          points.push({ icon: '⭐', text: 'در حد نو', highlight: true });
        }
      }
      
      // Automotive specific highlights
      if (template.layout === 'automotive') {
        if (name.includes('mileage') || name.includes('km')) {
          const mileage = parseInt(attr.value.replace(/\D/g, ''));
          if (!isNaN(mileage) && mileage < 50000) {
            points.push({ icon: '🛣️', text: `${mileage.toLocaleString()} km`, highlight: true });
          } else if (!isNaN(mileage)) {
            points.push({ icon: '🛣️', text: `${mileage.toLocaleString()} km`, highlight: false });
          }
        }
        if (name.includes('year')) {
          const year = parseInt(attr.value);
          const currentYear = new Date().getFullYear();
          if (!isNaN(year)) {
            points.push({ 
              icon: '📅', 
              text: `مدل ${year}`, 
              highlight: (currentYear - year) <= 3 
            });
          }
        }
        if (name.includes('fuel')) {
          points.push({ 
            icon: '⛽', 
            text: attr.value,
            highlight: value.includes('hybrid') || value.includes('electric')
          });
        }
      }
      
      // Electronics specific highlights
      else if (template.layout === 'electronics') {
        if (name.includes('warranty') && attr.value) {
          points.push({ icon: '🛡️', text: `گارانتی ${attr.value}`, highlight: true });
        }
        if (name.includes('brand')) {
          const premiumBrands = ['apple', 'samsung', 'sony', 'microsoft', 'dell', 'lg', 'asus'];
          points.push({ 
            icon: '🏷️', 
            text: attr.value,
            highlight: premiumBrands.some(brand => value.includes(brand))
          });
        }
        if (name.includes('storage') && attr.value) {
          points.push({ icon: '💾', text: attr.value, highlight: false });
        }
      }
      
      // Real Estate specific highlights
      else if (template.layout === 'realestate') {
        if (name.includes('bedroom') && name.includes('bathroom')) {
          // This would be handled differently - you'd need separate bedroom/bathroom attributes
        } else if (name.includes('sqft') || name.includes('square')) {
          points.push({ icon: '📐', text: `${attr.value} sq ft`, highlight: false });
        }
        if (name.includes('parking') && attr.value) {
          points.push({ icon: '🚗', text: attr.value, highlight: true });
        }
      }
      
      // Fashion specific highlights
      else if (template.layout === 'fashion') {
        if (name.includes('brand')) {
          const luxuryBrands = ['gucci', 'prada', 'louis vuitton', 'chanel', 'dior', 'versace'];
          points.push({ 
            icon: '👜', 
            text: attr.value,
            highlight: luxuryBrands.some(brand => value.includes(brand))
          });
        }
        if (name.includes('size') && attr.value) {
          points.push({ icon: '📏', text: `سایز ${attr.value}`, highlight: false });
        }
      }
      
      // Generic highlights for important attributes
      if (name.includes('warranty') && !points.some(p => p.text.includes('گارانتی'))) {
        points.push({ icon: '🛡️', text: attr.value, highlight: true });
      }
    });
    
    return points.slice(0, 6); // Limit to 6 points to avoid clutter
  };

  const sellingPoints = generateSellingPoints();
  
  if (sellingPoints.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-primary-200 rounded-lg p-6">
      <h3 className="font-semibold text-primary-900 mb-4 flex items-center">
        🌟 نکات کلیدی
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sellingPoints.map((point, index) => (
          <div
            key={index}
            className={`flex items-center space-x-2 space-x-reverse p-3 rounded-lg ${
              point.highlight
                ? 'bg-yellow-100 border border-yellow-300 text-yellow-800'
                : 'bg-white border border-gray-200 text-gray-700'
            }`}
          >
            <span className="text-lg">{point.icon}</span>
            <span className="font-medium text-sm">{point.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to get category template (simplified)
const getCategoryTemplate = (categoryName: string): CategoryTemplate => {
  const categoryKey = categoryName.toLowerCase().replace(/\s+/g, '-');
  
  if (categoryKey.includes('car') || categoryKey.includes('vehicle') || categoryKey.includes('auto') || categoryKey.includes('motorcycle')) {
    return CATEGORY_TEMPLATES['automotive'];
  }
  if (categoryKey.includes('electronics') || categoryKey.includes('phone') || categoryKey.includes('computer') || categoryKey.includes('tablet')) {
    return CATEGORY_TEMPLATES['electronics'];
  }
  if (categoryKey.includes('real-estate') || categoryKey.includes('property') || categoryKey.includes('apartment') || categoryKey.includes('house')) {
    return CATEGORY_TEMPLATES['realestate'];
  }
  if (categoryKey.includes('clothing') || categoryKey.includes('fashion') || categoryKey.includes('shoes') || categoryKey.includes('accessories')) {
    return CATEGORY_TEMPLATES['fashion'];
  }
  
  return {
    name: 'عمومی',
    layout: 'default',
    sections: { 'مشخصات': [] }
  };
};

// Render dynamic category specifications
const renderCategorySpecs = (categoryName: string, attributes: AdAttribute[]) => {
  if (!attributes.length) return null;
  
  const template = getCategoryTemplate(categoryName);
  return (
    <DynamicCategorySpecs 
      categoryName={categoryName}
      attributes={attributes}
      layout={template.layout}
    />
  );
};

// Business Page Button Component
const BusinessPageButton: React.FC<{ 
  businessSlug?: string; 
  businessApprovalStatus?: number | string; 
}> = ({ businessSlug, businessApprovalStatus }) => {
  // Hide button if business doesn't have slug or is not approved
  if (!businessSlug || (businessApprovalStatus !== 1 && businessApprovalStatus !== 'Approved')) {
    return null;
  }

  return (
    <div className="mb-4">
      <Link
        to={`/business/${businessSlug}`}
        className="group relative w-full bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-600 text-white py-3 px-4 rounded-xl hover:from-purple-700 hover:via-purple-800 hover:to-indigo-700 flex items-center justify-center space-x-2 font-semibold transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg shadow-md overflow-hidden"
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 via-transparent to-indigo-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Shimmer effect */}
        <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-500"></div>
        
        {/* Content */}
        <div className="relative flex items-center space-x-2">
          <div className="p-1 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors duration-300">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <span className="text-lg">🏪 مشاهده فروشگاه</span>
          <div className="opacity-70 group-hover:opacity-100 transition-opacity duration-300">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </div>
        </div>
        
        {/* Border glow effect */}
        <div className="absolute inset-0 rounded-xl border-2 border-white/20 group-hover:border-white/40 transition-colors duration-300"></div>
      </Link>
    </div>
  );
};

const AdDetailsPage: React.FC = () => {
  const { id, slug } = useParams<{ id?: string; slug?: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showingPhoneNumber, setShowingPhoneNumber] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportDescription, setReportDescription] = useState('');
  const [boostingRanking, setBoostingRanking] = useState(false);
  const [markingAsSold, setMarkingAsSold] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);
  // Fallback map coordinates when ad doesn't include latitude/longitude
  const [mapCoords, setMapCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [mapGeocodeLoading, setMapGeocodeLoading] = useState(false);


  // Determine which identifier to use and create appropriate query key
  const identifier = slug || id;
  const isSlugRoute = !!slug;

  const { data: ad, isLoading, error } = useQuery({
    queryKey: ['ad', identifier, isSlugRoute ? 'slug' : 'id'],
    queryFn: () => isSlugRoute ? adsApi.getBySlug(slug!) : adsApi.getById(id!),
    enabled: !!identifier
  });

  const { data: relatedAds } = useQuery({
    queryKey: ['related-ads', ad?.data?.categoryId, ad?.data?.id],
    queryFn: () => adsApi.search({
      categoryId: ad?.data?.categoryId,
      excludeAdId: ad?.data?.id, // Exclude only the current ad itself, not all user's ads
      pageSize: 4
    }),
    enabled: !!ad?.data?.categoryId
  });

  const { data: sellerAds } = useQuery({
    queryKey: ['seller-ads', ad?.data?.userId],
    queryFn: () => adsApi.search({
      userId: ad?.data?.userId,
      pageSize: 6
    }),
    enabled: !!ad?.data?.userId
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: () => adsApi.toggleFavorite(ad?.data?.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ad', identifier, isSlugRoute ? 'slug' : 'id'] });
              toast.success('لیست علاقه‌مندی‌ها با موفقیت به‌روزرسانی شد.');
    },
    onError: () => {
              toast.error('خطا در به‌روزرسانی علاقه‌مندی‌ها');
    }
  });

  // If user toggles the map and the ad lacks coordinates, try a lightweight Nominatim lookup
  useEffect(() => {
    if (!showMap) return;

    // If ad already has coords, nothing to do
    if (ad?.data?.latitude && ad?.data?.longitude) return;

    // Avoid repeated lookups
    if (mapCoords || mapGeocodeLoading) return;

    const parts: string[] = [];
    if (ad?.data?.postalCode) parts.push(ad.data.postalCode);
    if (ad?.data?.address) parts.push(ad.data.address);
    if (ad?.data?.city) parts.push(ad.data.city);
    if (ad?.data?.province) parts.push(ad.data.province);
    parts.push('Canada');

    const q = parts.filter(Boolean).join(', ');
    if (!q) return;

    const controller = new AbortController();

    (async () => {
      try {
        setMapGeocodeLoading(true);
        let url = '';
        let results: any[] = [];

        // Build unified address string
        const street = ad?.data?.address || '';
        const city = ad?.data?.city || '';
        const state = ad?.data?.province || '';
        const postal = ad?.data?.postalCode || '';
        const fullAddress = `${street}, ${city}, ${state}, ${postal}, Canada`;

        // --- Try Nominatim (OpenStreetMap) first ---
        url = `https://nominatim.openstreetmap.org/search?format=json&limit=3&q=${encodeURIComponent(fullAddress)}&countrycodes=ca`;

        const res = await fetch(url, {
          signal: controller.signal,
          headers: { 'Accept-Language': 'en', 'User-Agent': 'TimchehApp/1.0 (https://tvtobook.com)' },
        });

        if (res.ok) {
          results = await res.json();
        }

        // --- If Nominatim failed or returned nothing, try Geocoder.ca fallback ---
        if (!results || !results.length) {
          if (postal) {
            const geoUrl = `https://geocoder.ca/?postal=${encodeURIComponent(postal)}&geoit=json`;
            const geoRes = await fetch(geoUrl);
            if (geoRes.ok) {
              const geoData = await geoRes.json();
              if (geoData.latt && geoData.longt) {
                setMapCoords({ lat: Number(geoData.latt), lon: Number(geoData.longt) });
                return;
              }
            }
          }
        }

        // --- If we got valid Nominatim results ---
        if (results && results.length) {
          const normalizePostal = (p: string | undefined) => (p || '').toUpperCase().replace(/\s+/g, '');
          const adPostal = normalizePostal(postal);
          const rawAddress = street || '';
          const streetMatch = rawAddress.match(/^\s*(\d+)\s+(.+)$/);
          const streetName = streetMatch ? streetMatch[2].split(',')[0].trim().toLowerCase() : rawAddress.split(',')[0].trim().toLowerCase();

          let chosen = results[0];

          // Prefer same postal code match
          for (const cand of results) {
            const candPostal = normalizePostal(cand?.address?.postcode);
            if (adPostal && candPostal && adPostal === candPostal) {
              chosen = cand;
              break;
            }
          }

          // If still default, try road/street name match
          if (chosen === results[0] && streetName) {
            for (const cand of results) {
              const road = (cand?.address?.road || cand?.address?.pedestrian || cand?.address?.footway || '').toLowerCase();
              if (road.includes(streetName)) {
                chosen = cand;
                break;
              }
            }
          }

          const lat = Number(chosen.lat);
          const lon = Number(chosen.lon);
          if (!isNaN(lat) && !isNaN(lon)) {
            setMapCoords({ lat, lon });
          }
        }
      } catch (e) {
        console.warn('Geocoding failed or aborted:', e);
      } finally {
        setMapGeocodeLoading(false);
      }
    })();

    return () => controller.abort();
  }, [showMap, ad, mapCoords, mapGeocodeLoading]);

  // Note: View tracking is handled automatically by the getById/getBySlug endpoints
  // No need for separate incrementView call

  const handleToggleFavorite = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    toggleFavoriteMutation.mutate();
  };

  const handleShare = async () => {
    setShowShareModal(true);
  };

  const handleShareOption = async (platform: string) => {
    const url = window.location.href;
    const title = ad?.data?.title || '';
    const description = ad?.data?.description || '';

    switch (platform) {
      case 'copy':
        navigator.clipboard.writeText(url);
        toast.success('لینک در کلیپ‌بورد کپی شد!');
        break;
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description + '\n\n' + url)}`);
        break;
      case 'native':
        if ('share' in navigator) {
          try {
            await navigator.share({
              title: title,
              text: description,
              url: url
            });
          } catch (error) {
            // User cancelled sharing
          }
        }
        break;
    }
    setShowShareModal(false);
  };

  const handleContactSeller = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setShowContactInfo(true);
  };

  const handleQuickContact = () => {
    setShowContactInfo(true);
  };

  const handleShowPhoneNumber = async () => {
    if (showContactInfo) {
      setShowContactInfo(false);
      return;
    }
    
    setShowingPhoneNumber(true);
    
    // Simulate a brief delay for loading effect (you can remove this if not needed)
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setShowContactInfo(true);
    setShowingPhoneNumber(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBumpUpAd = async () => {
    if (!isAuthenticated || !ad?.data?.id) {
      return;
    }

    setBoostingRanking(true);
    try {
      const response = await adsApi.bumpUpAd(ad.data.id);
      toast.success(response.data.message);
      // Refresh the ad data to show updated position
      queryClient.invalidateQueries({ queryKey: ['ad', identifier, isSlugRoute ? 'slug' : 'id'] });
    } catch (error: any) {
              toast.error(error.response?.data?.message || 'خطا در تقویت آگهی');
    } finally {
      setBoostingRanking(false);
    }
  };

  const handleMarkAsSold = async () => {
    if (!isAuthenticated || !ad?.data?.id) {
      return;
    }

    setMarkingAsSold(true);
    try {
      const response = await adsApi.markAsSold(ad.data.id);
      toast.success(response.data.message);
      // Refresh the ad data to show updated status
      queryClient.invalidateQueries({ queryKey: ['ad', identifier, isSlugRoute ? 'slug' : 'id'] });
    } catch (error: any) {
              toast.error(error.response?.data?.message || 'خطا در علامت‌گذاری آگهی به عنوان فروخته شده');
    } finally {
      setMarkingAsSold(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(price);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const ImageLightbox = () => {
    if (!showLightbox || !ad?.data.images.length) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
        <div className="relative max-w-5xl mx-auto p-4">
            <button
              onClick={() => setShowLightbox(false)}
              className="absolute top-4 left-4 text-white hover:text-gray-300 z-10"
            >
              <XMarkIcon className="h-8 w-8" />
          </button>
          
          <img
            src={ad.data.images[selectedImageIndex]?.url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5Y2EzYWYiIGZvbnQtZmFtaWx5PSJtb25vc3BhY2UiIGZvbnQtc2l6ZT0iMTNweCI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPg=='}
            alt={ad.data.title}
            className="max-h-[80vh] max-w-full object-contain"
          />
          
          {ad.data.images.length > 1 && (
            <>
              <button
                onClick={() => setSelectedImageIndex(
                  selectedImageIndex > 0 ? selectedImageIndex - 1 : ad.data.images.length - 1
                )}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
              >
                <ChevronRightIcon className="h-10 w-10" />
              </button>
              
              <button
                onClick={() => setSelectedImageIndex(
                  selectedImageIndex < ad.data.images.length - 1 ? selectedImageIndex + 1 : 0
                )}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
              >
                <ChevronLeftIcon className="h-10 w-10" />
              </button>
              
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
                {selectedImageIndex + 1} / {ad.data.images.length}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 animate-pulse">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-gray-200 rounded-lg h-96"></div>
              <div className="space-y-4">
                <div className="bg-gray-200 rounded h-8 w-3/4"></div>
                <div className="bg-gray-200 rounded h-4 w-1/4"></div>
                <div className="space-y-2">
                  <div className="bg-gray-200 rounded h-4"></div>
                  <div className="bg-gray-200 rounded h-4"></div>
                  <div className="bg-gray-200 rounded h-4 w-3/4"></div>
                </div>
              </div>
            </div>
            <div className="bg-gray-200 rounded-lg h-64"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !ad?.data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center px-4 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-lg w-full relative z-10">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-10 text-center relative overflow-hidden">
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent"></div>
            
            {/* Content */}
            <div className="relative z-10">
              {/* Icon with animation */}
              <div className="mx-auto w-28 h-28 bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 rounded-full flex items-center justify-center mb-8 relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-lg group-hover:blur-xl transition-all duration-300"></div>
                <div className="relative bg-white/70 rounded-full p-4 backdrop-blur-sm">
                  <svg className="w-10 h-10 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              
              {/* 404 number */}
              <div className="text-6xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-slate-600 to-slate-800 mb-2">
                404
              </div>
              
              {/* Title */}
                <h1 className="text-3xl font-semibold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-4">
                آگهی یافت نشد
              </h1>
              
              {/* Description */}
              <div className="text-slate-600 mb-10 leading-relaxed space-y-2">
                                  <p className="text-lg">آگهی که به دنبال آن هستید وجود ندارد یا حذف شده است.</p>
                <p className="text-sm text-slate-500">لینک ممکن است نادرست باشد یا آگهی منقضی شده باشد</p>
              </div>
              
              {/* Actions */}
              <div className="space-y-4">
                <Link 
                  to="/search" 
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-8 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group"
                >
                  <svg className="w-5 h-5 ml-3 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  جستجوی آگهی‌ها
                </Link>
                
                <Link 
                  to="/" 
                  className="w-full bg-white/70 backdrop-blur-sm border-2 border-slate-200 text-slate-700 py-4 px-8 rounded-xl hover:bg-white hover:border-slate-300 transition-all duration-300 flex items-center justify-center font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 group"
                >
                  <svg className="w-5 h-5 ml-3 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  بازگشت به خانه
                </Link>
              </div>
              
              {/* Footer */}
              <div className="mt-10 pt-8 border-t border-slate-200/50">
                <p className="text-sm text-slate-500 leading-relaxed">
                  اگر فکر می‌کنید این خطا نادرست است،
                  <br />
                  لطفاً با تیم پشتیبانی تماس بگیرید.
                </p>
              </div>
            </div>
          </div>
          
          {/* Floating elements */}
          <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full opacity-60 animate-pulse"></div>
          <div className="absolute -bottom-6 -left-6 w-6 h-6 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full opacity-40 animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </div>
    );
  }

  const adData = ad.data;
  const isOwner = user?.id === adData.userId;
  

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 sm:gap-4 overflow-x-auto scrollbar-hide">
              <li className="flex-shrink-0">
                <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm sm:text-base">خانه</Link>
              </li>
              <li className="flex-shrink-0">
                <span className="text-gray-400 text-sm sm:text-base">/</span>
              </li>
              <li className="flex-shrink-0">
                <Link to="/search" className="text-gray-500 hover:text-gray-700 text-sm sm:text-base">جستجو</Link>
              </li>
              <li className="flex-shrink-0">
                <span className="text-gray-400 text-sm sm:text-base">/</span>
              </li>
              <li className="flex-shrink-0">
                <Link 
                  to={`/search?category=${adData.categoryId}`} 
                  className="text-gray-500 hover:text-gray-700 text-sm sm:text-base"
                >
                  {adData.categoryName}
                </Link>
              </li>
              <li className="flex-shrink-0">
                <span className="text-gray-400 text-sm sm:text-base">/</span>
              </li>
              <li className="text-gray-900 font-medium truncate max-w-32 sm:max-w-xs text-sm sm:text-base">
                {adData.title}
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Header with Price and Actions */}
            <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 ${
              adData.isHighlighted || adData.isUrgent || adData.isTopAd || adData.isFeatured ? 
              'promoted-hover ' + 
              (adData.isHighlighted ? 'glow-highlighted shimmer-effect float-effect ' : '') +
              (adData.isUrgent ? 'glow-urgent animate-pulse-gentle ' : '') +
              (adData.isTopAd ? 'glow-top-ad rainbow-border ' : '') +
              (adData.isFeatured ? 'glow-featured highlighted-gradient-border ' : '')
              : ''
            }`}>
              <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-start sm:justify-between mb-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {adData.status === 'Sold' && (
                      <span className="bg-blue-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                        ✅ فروخته شد
                      </span>
                    )}
                    {adData.isFeatured && (
                      <span className="bg-yellow-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                        ⭐ ویژه
                      </span>
                    )}
                    {adData.isUrgent && (
                      <span className="bg-red-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                        🔥 فوری
                      </span>
                    )}
                    {adData.isHighlighted && (
                      <span className="bg-orange-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                        ✨ برجسته
                      </span>
                    )}
                    {adData.isTopAd && (
                      <span className="bg-purple-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                        💎 آگهی برتر
                      </span>
                    )}
                  </div>
                  <h1 className={`text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 mb-3 leading-tight ${
                    adData.isHighlighted ? 'neon-text' : ''
                  }`}>{adData.title}</h1>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <span className="text-2xl sm:text-3xl font-semibold text-green-600">
                      {formatPrice(adData.price)}
                    </span>
                    {adData.isNegotiable && (
                      <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded-md text-xs sm:text-sm font-medium w-fit">
                        قابل مذاکره
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 sm:gap-3 sm:mt-0">
                  <button
                    onClick={handleToggleFavorite}
                    className={`p-2 sm:p-3 rounded-lg border-2 transition-colors ${
                      adData.isFavorited 
                        ? 'border-red-300 bg-red-50 text-red-600' 
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {adData.isFavorited ? (
                      <HeartIconSolid className="h-5 w-5 sm:h-6 sm:w-6" />
                    ) : (
                      <HeartIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    )}
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-2 sm:p-3 border-2 border-gray-300 rounded-lg bg-white text-gray-600 hover:bg-gray-50"
                  >
                    <ShareIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                  <button
                    onClick={handlePrint}
                    className="p-2 sm:p-3 border-2 border-gray-300 rounded-lg bg-white text-gray-600 hover:bg-gray-50"
                  >
                    <PrinterIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                  {!isOwner && (
                    <button 
                      onClick={() => setShowReportModal(true)}
                      className="p-2 sm:p-3 border-2 border-gray-300 rounded-lg bg-white text-gray-600 hover:bg-gray-50"
                      title="گزارش آگهی"
                    >
                      <FlagIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  )}
                </div>
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <MapPinIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{adData.city}, {adData.province}</span>
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <ClockIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{formatDate(adData.datePosted)}</span>
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                                      <EyeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>{adData.viewCount} بازدید</span>
                </div>
              </div>
            </div>

            {/* Image Gallery */}
            <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${
              adData.isHighlighted ? 'glow-highlighted' : 
              adData.isUrgent ? 'glow-urgent' : 
              adData.isTopAd ? 'glow-top-ad' : 
              adData.isFeatured ? 'glow-featured' : ''
            }`}>
              <div className="relative">
                <img
                  src={adData.images[selectedImageIndex]?.url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5Y2EzYWYiIGZvbnQtZmFtaWx5PSJtb25vc3BhY2UiIGZvbnQtc2l6ZT0iMTNweCI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPg=='}
                  alt={adData.title}
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover cursor-pointer"
                  onClick={() => setShowLightbox(true)}
                />
                <button
                  onClick={() => setShowLightbox(true)}
                  className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-black bg-opacity-50 text-white p-1.5 sm:p-2 rounded-lg hover:bg-opacity-70"
                >
                  <MagnifyingGlassPlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                {adData.images.length > 1 && (
                  <>
                    <button
                      onClick={() => setSelectedImageIndex(
                        selectedImageIndex > 0 ? selectedImageIndex - 1 : adData.images.length - 1
                      )}
                      className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-1.5 sm:p-2 rounded-lg hover:bg-opacity-70"
                    >
                      <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                    <button
                      onClick={() => setSelectedImageIndex(
                        selectedImageIndex < adData.images.length - 1 ? selectedImageIndex + 1 : 0
                      )}
                      className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-1.5 sm:p-2 rounded-lg hover:bg-opacity-70"
                    >
                      <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  </>
                )}
              </div>
              
              {adData.images.length > 1 && (
                <div className="p-3 sm:p-4">
                  <div className="flex gap-2 sm:gap-3 overflow-x-auto scrollbar-hide">
                    {adData.images.map((image, index) => (
                      <button
                        key={image.id}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 ${
                          selectedImageIndex === index ? 'border-primary-500' : 'border-gray-200'
                        }`}
                      >
                        <img
                          src={image.thumbnailUrl || image.url}
                          alt={`${adData.title} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Disclaimer */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 text-center leading-relaxed">
                تیمچه هیچ اطلاعاتی در مورد هویت افرادی که آگهی می‌گذارند یا مشاهده می‌کنند ندارد. بنابراین، کاربران باید اقدامات احتیاطی لازم را برای اطمینان از امنیت خود انجام دهند و این مسئولیت کاملاً بر عهده آن‌هاست.
              </p>
            </div>

            {/* Category-specific Selling Points */}
            <CategorySellingPoints 
              categoryName={adData.categoryName}
              attributes={adData.attributes}
              price={adData.price}
            />

            {/* Description */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">توضیحات</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-line leading-relaxed text-sm sm:text-base">
                  {adData.description}
                </p>
              </div>
            </div>

            {/* Specifications - Category-specific */}
            {adData.attributes.length > 0 && (
              <>
                {renderCategorySpecs(adData.categoryName, adData.attributes) || (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">مشخصات</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {adData.attributes.map((attr) => (
                        <div key={attr.id} className="border-b border-gray-100 py-2 sm:py-3">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
                            <span className="font-medium text-gray-700 text-sm sm:text-base">{attr.label}:</span>
                            <span className="text-gray-600 font-semibold text-sm sm:text-base">{attr.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Location & Map */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">مکان</h2>
                <button
                  onClick={() => setShowMap(!showMap)}
                  className="text-primary-600 hover:text-primary-700 text-xs sm:text-sm font-medium"
                >
                                      {showMap ? 'مخفی کردن نقشه' : 'نمایش نقشه'}
                </button>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center text-gray-600">
                  <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base">{adData.address || `${adData.city}, ${adData.province}`}</span>
                </div>
                <div className="text-xs sm:text-sm text-gray-500 mr-6 sm:mr-8">
                  {adData.city}, {adData.province} {adData.postalCode}
                </div>
              </div>

{showMap && (
  <div className="mt-4 h-48 sm:h-64 bg-white rounded-lg overflow-hidden border border-gray-200">
    {adData.postalCode || adData.city ? (
      <iframe
        title="Map location"
        width="100%"
        height="100%"
        style={{ border: 0 }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        // ✅ Center + marker in one embed (no API key)
        src={`https://www.google.com/maps?q=${encodeURIComponent(
          `${adData.postalCode || ''}, ${adData.city || ''}, ${adData.province || ''}, Canada`
        )}&z=14&output=embed&markers=${encodeURIComponent(
          `${adData.postalCode || ''}, ${adData.city || ''}, ${adData.province || ''}, Canada`
        )}`}
        allowFullScreen
      />
    ) : (
      <div className="h-full flex items-center justify-center text-gray-500 text-sm">
        مکان در دسترس نیست
      </div>
    )}
  </div>
)}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Seller Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center ml-3 sm:ml-4 flex-shrink-0">
                  <span className="text-white text-lg sm:text-xl font-semibold">
                    {adData.userName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{adData.userName}</h3>
                    {adData.userIsVerified && (
                      <ShieldCheckIconSolid className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-gray-600">{adData.userType === 'Business' ? 'تجاری' : 'شخصی'}</span>
                    {adData.userIsVerified && adData.userType === 'Business' && (
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium w-fit">
                                                  تأیید شده
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Page Button */}
              {adData.userType === 'Business' && !isOwner && (
                <BusinessPageButton 
                  businessSlug={adData.businessSlug} 
                  businessApprovalStatus={adData.businessApprovalStatus} 
                />
              )}

              {!isOwner && (
                <div className="space-y-2 sm:space-y-3">
                  <button
                    onClick={handleContactSeller}
                    disabled={adData.status === 'Sold'}
                    className="w-full bg-primary-600 text-white py-2.5 sm:py-3 px-4 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 text-sm sm:text-base"
                  >
                    <ChatBubbleLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>{adData.status === 'Sold' ? 'آگهی فروخته شد' : 'پیام به فروشنده'}</span>
                  </button>
                  
                  {showContactInfo && (
                    <div className="space-y-2 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      {adData.allowPhoneContact && adData.userPhoneNumber && adData.status !== 'Sold' && (
                        <a                          
                          className="flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm sm:text-base"
                        >
                          <PhoneIcon className="h-4 w-4" />
                          <span>{adData.userPhoneNumber}</span>
                        </a>
                      )}
                      {adData.status === 'Sold' && (
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <CheckBadgeIcon className="h-4 w-4" />
                          <span>این آگهی فروخته شده و تماس دیگر در دسترس نیست</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <button 
                    onClick={handleShowPhoneNumber}
                    disabled={showingPhoneNumber || adData.status === 'Sold'}
                    className="w-full border border-primary-600 text-primary-600 py-2 sm:py-2.5 px-4 rounded-lg hover:bg-primary-50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {showingPhoneNumber ? (
                      <div className="flex items-center gap-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-top-transparent"></div>
                            <span>در حال اعمال...</span>
                      </div>
                    ) : adData.status === 'Sold' ? (
                      <>
                        <CheckBadgeIcon className="h-4 w-4" />
                        <span>آگهی فروخته شد</span>
                      </>
                    ) : (
                      <>
                        <PhoneIcon className="h-4 w-4" />
                        <span>نمایش شماره تلفن</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {isOwner && (
                <div className="space-y-3">
                  <Link
                    to={`/edit-ad/${adData.id}`}
                    className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 text-center block"
                  >
                    ویرایش آگهی
                  </Link>
                  <button 
                    onClick={handleBumpUpAd}
                    disabled={boostingRanking}
                    className="w-full border border-purple-600 text-purple-600 py-2 px-4 rounded-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 space-x-reverse"
                  >
                    {boostingRanking ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-top-transparent"></div>
                        <span>در حال ترویج...</span>
                      </>
                    ) : (
                      <>
                        <ArrowTrendingUpIcon className="h-4 w-4" />
                        <span>تقویت آگهی</span>
                      </>
                    )}
                  </button>
                  <button 
                    onClick={handleMarkAsSold}
                    disabled={markingAsSold || adData.status === 'Sold'}
                    className="w-full border border-green-600 text-green-600 py-2 px-4 rounded-lg hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 space-x-reverse"
                  >
                    {markingAsSold ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-top-transparent"></div>
                        <span>در حال علامت‌گذاری...</span>
                      </>
                    ) : adData.status === 'Sold' ? (
                      <>
                        <CheckBadgeIcon className="h-4 w-4" />
                        <span>فروخته شد</span>
                      </>
                    ) : (
                      <>
                        <CheckBadgeIcon className="h-4 w-4" />
                        <span>علامت‌گذاری به عنوان فروخته شده</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Safety Tips */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4 sm:p-6">
              <div className="flex items-center mb-3">
                                  <ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 mr-2 flex-shrink-0" />
                                  <h3 className="font-semibold text-yellow-800 text-sm sm:text-base">نکات امنیتی</h3>
              </div>
              <ul className="text-xs sm:text-sm text-yellow-700 space-y-2">
                <li className="flex items-start">
                  <CheckBadgeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                  <span>در مکان عمومی ملاقات کنید</span>
                </li>
                <li className="flex items-start">
                  <CheckBadgeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                  <span>قبل از خرید کالا را بررسی کنید</span>
                </li>
                <li className="flex items-start">
                  <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 mt-0.5 text-red-600 flex-shrink-0" />
                  <span>هرگز پول را از قبل ارسال نکنید</span>
                </li>
              </ul>
            </div>

            {/* Ad Statistics */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 text-sm sm:text-base">اطلاعات آگهی</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center text-xs sm:text-sm">
                    <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                    بازدیدها:
                  </span>
                  <span className="font-medium text-xs sm:text-sm">{adData.viewCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center text-xs sm:text-sm">
                    <CalendarIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                                          منتشر شده:
                  </span>
                  <span className="font-medium text-xs sm:text-sm">{formatDate(adData.datePosted)}</span>
                </div>
                {adData.dateUpdated && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center text-xs sm:text-sm">
                      <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                                              به‌روزرسانی شده:
                    </span>
                    <span className="font-medium text-xs sm:text-sm">{formatDate(adData.dateUpdated)}</span>
                  </div>
                )}
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 text-xs sm:text-sm">شناسه آگهی:</span>
                  <div className="flex flex-col items-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(window.location.href)}`}
                      alt="QR Code for Ad"
                      className="w-12 h-12 sm:w-16 sm:h-16 border border-gray-200 rounded"
                                              title="اسکن برای مشاهده آگهی در موبایل"
                    />
                                          <span className="text-xs text-gray-500 mt-1">اسکن QR</span>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>

        {/* Related Ads Section */}
        {relatedAds?.data?.ads && relatedAds.data.ads.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">آگهی‌های مشابه</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedAds.data.ads.slice(0, 4).map((relatedAd) => (
                <Link key={relatedAd.id} to={`/ads/${relatedAd.slug || relatedAd.id}`} className="group">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                    <img
                      src={getImageUrl(relatedAd.primaryImageUrl)}
                      alt={relatedAd.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <h3 className="font-medium text-gray-900 line-clamp-2 group-hover:text-primary-600 mb-2">
                        {relatedAd.title}
                      </h3>
                      <div className="text-lg font-semibold text-green-600 mb-2">
                        {formatPrice(relatedAd.price)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {relatedAd.city}, {relatedAd.province}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <ImageLightbox />
      
      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">اشتراک‌گذاری آگهی</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <button
                onClick={() => handleShareOption('copy')}
                className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                  📋
                </div>
                <span className="text-xs sm:text-sm font-medium">کپی لینک</span>
              </button>
              
              <button
                onClick={() => handleShareOption('telegram')}
                className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                  ✈️
                </div>
                <span className="text-xs sm:text-sm font-medium">تلگرام</span>
              </button>
              
              <button
                onClick={() => handleShareOption('whatsapp')}
                className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center mb-2">
                  💬
                </div>
                <span className="text-xs sm:text-sm font-medium">واتساپ</span>
              </button>
              
              <button
                onClick={() => handleShareOption('twitter')}
                className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                  🐦
                </div>
                <span className="text-xs sm:text-sm font-medium">توییتر</span>
              </button>
              
              <button
                onClick={() => handleShareOption('facebook')}
                className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                  📘
                </div>
                <span className="text-xs sm:text-sm font-medium">فیسبوک</span>
              </button>
              
              <button
                onClick={() => handleShareOption('email')}
                className="flex flex-col items-center p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                  ✉️
                </div>
                <span className="text-xs sm:text-sm font-medium">ایمیل</span>
              </button>
            </div>
            
            {'share' in navigator && (
              <button
                onClick={() => handleShareOption('native')}
                className="w-full mt-3 sm:mt-4 bg-primary-600 text-white py-2.5 sm:py-3 rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
              >
                استفاده از اشتراک‌گذاری سیستم
              </button>
            )}
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">گزارش آگهی</h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
            
            <p className="text-gray-600 mb-4 text-sm sm:text-base">
              لطفاً دلیل گزارش این آگهی را انتخاب کنید:
            </p>

            {/* Description textarea */}
            <div className="mb-4">
              <label htmlFor="reportDescription" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                جزئیات بیشتر (اختیاری):
              </label>
              <textarea
                id="reportDescription"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="در صورت نیاز، جزئیات بیشتری در مورد مشکل ارائه دهید..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none text-sm"
                rows={3}
                maxLength={500}
              />
              <div className="text-xs text-gray-500 mt-1">
                {reportDescription.length}/500 کاراکتر
              </div>
            </div>
            
            <div className="space-y-2">
              {[
                'محتوای نامناسب یا توهین‌آمیز',
                'اطلاعات نادرست یا گمراه‌کننده',
                'قیمت غیرمنطقی',
                'تصاویر نامرتبط با محصول',
                'لیست تکراری',
                'اقلام غیرقانونی برای فروش',
                'کلاهبرداری یا تقلب',
                'سایر'
              ].map((reason, index) => (
                <button
                  key={index}
                  onClick={async () => {
                    if (!ad?.data?.id || submittingReport) return;
                    
                    setSubmittingReport(true);
                    const currentDescription = reportDescription.trim();
                    const reasonMap: Record<string, string> = {
                      'محتوای نامناسب یا توهین‌آمیز': 'InappropriateContent',
                      'اطلاعات نادرست یا گمراه‌کننده': 'FalseInformation',
                      'قیمت غیرمنطقی': 'UnreasonablePrice',
                      'تصاویر نامرتبط با محصول': 'UnrelatedImages',
                      'لیست تکراری': 'DuplicateListing',
                      'اقلام غیرقانونی برای فروش': 'IllegalGoods',
                      'کلاهبرداری یا تقلب': 'FraudOrScam',
                      'سایر': 'Other'
                    };

                    try {
                      console.log('Sending report:', {
                        adId: ad.data.id,
                        reason: reasonMap[reason] || 8,
                        description: currentDescription || null
                      });

                      const response = await api.post('/adreport', {
                        adId: ad.data.id,
                        reason: reasonMap[reason] || 'Other',
                        description: currentDescription || null
                      });

                      toast.success('گزارش شما با موفقیت ارسال شد و بررسی خواهد شد');
                      setShowReportModal(false);
                      setReportDescription(''); // Clear description after successful submission
                    } catch (error: any) {
                      console.error('Report submission error:', error);
                      
                      if (error?.response?.status === 401) {
                        toast.error('برای ارسال گزارش باید وارد شوید');
                      } else if (error?.response?.status === 400) {
                        const errorMessage = error?.response?.data;
                        if (typeof errorMessage === 'string') {
                          toast.error(errorMessage);
                        } else if (errorMessage?.message) {
                          toast.error(errorMessage.message);
                        } else if (errorMessage && typeof errorMessage === 'object') {
                          // Handle ModelState validation errors
                          const validationErrors = Object.values(errorMessage).flat();
                          const firstError = validationErrors.length > 0 ? String(validationErrors[0]) : 'داده‌های گزارش نامعتبر';
                          toast.error(firstError);
                        } else {
                          toast.error('گزارش قبلاً برای این آگهی ارسال شده یا آگهی وجود ندارد');
                        }
                      } else if (error?.response?.status === 500) {
                        toast.error('خطای سرور. لطفاً بعداً دوباره تلاش کنید.');
                      } else if (error?.code === 'NETWORK_ERROR' || !error?.response) {
                        toast.error('خطای شبکه. لطفاً اتصال خود را بررسی کنید و دوباره تلاش کنید.');
                                             } else {
                         toast.error(error?.response?.data?.message || error?.message || 'ارسال گزارش ناموفق بود. لطفاً دوباره تلاش کنید.');
                       }
                     } finally {
                       setSubmittingReport(false);
                     }
                  }}
                  className={`w-full text-right p-2.5 sm:p-3 border border-gray-200 rounded-lg transition-colors text-sm sm:text-base ${
                    submittingReport 
                      ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                      : 'hover:bg-gray-50 cursor-pointer'
                  }`}
                  disabled={submittingReport}
                >
                  {submittingReport ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-top-transparent ml-2"></div>
                      در حال ارسال...
                    </div>
                  ) : (
                    reason
                  )}
                </button>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                گزارش‌های نادرست ممکن است منجر به محدودیت در حساب شما شود.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdDetailsPage; 
